'use client';

import React, { useEffect, useState, useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { EMAIL_REGEX } from '@/utils/regex';
import isEmailExists from '@/utils/validations/isEmailExists';
import useToast, { ToastNotification } from '@/hooks/useToast';
import ForgotPasswordForm from '@/app/[locale]/(welcome)/auth/(with_banner)/forgot-password/form';
import SentInfo from '@/app/[locale]/(welcome)/auth/(with_banner)/forgot-password/sentInfo';
import { resetPassword } from '@/actions/auth/resetPassword';
import { formatTitle } from '@/utils/formatTitle';

const steps = {
    request: 'request',
    sent: 'sent'
};

export default function ForgotPasswordPage() {
    const t = useTranslations();
    const [queueNotification] = useToast();
    const [, startTransition] = useTransition();
    const [step, setStep] = useState(steps.request);
    const [model, setModel] = useState({ email: '' });

    const initialFormValues = {
        ...model
    };

    const validationSchema = Yup.object({
        email: Yup.string().required(t('validation.missingEmail')).matches(EMAIL_REGEX, t('validation.email')).test('exists', t('validation.notFoundEmail'), value => new Promise(resolve => isEmailExists(value, resolve)))
    });

    const handleConfirm = (values, { setSubmitting }) => {
        startTransition(async () => {
            const success = await resetPassword(values.email).catch(console.error);

            if (success) {
                setModel({
                    email: values.email
                });
                setStep(steps.sent);
            } else {
                queueNotification({
                    description: <>{t('common.error')}</>
                } as ToastNotification);
            }

            setSubmitting(false);
        });
    };

    useEffect(() => {
        document.title = formatTitle([
            t('meta.titles.forgotPassword'),
            t('meta.titles.moonOne')
        ]);
    }, [t]);

    return (
        <>
            {step === steps.request && (
                <Formik
                  initialValues={initialFormValues}
                  onSubmit={handleConfirm}
                  validationSchema={validationSchema}
                >
                    {formikProps => <ForgotPasswordForm {...formikProps} />}
                </Formik>
            )}

            {step === steps.sent && (
                <SentInfo email={model.email} />
            )}
        </>
    );
}
